/**
 * A marketing email's unsubscribe link — one check, run wherever a marketing
 * email can come from (2026-10-23, marketing-unsubscribe-guarantee):
 *
 *   - when staff SAVE one: `edit_email_template` (for a marketing key) and
 *     `checkFlowLiveEdit` (for a marketing step's own HTML) run
 *     {@link checkMarketingEmailTemplate} and refuse what fails it;
 *   - when one is COMPOSED: a stored edit that fails the same check is not
 *     used (`email-template-compose.ts`), so a row saved before the rule, or
 *     written by SQL, never reaches a member;
 *   - when one is SENT: `send_email` runs {@link checkSentMarketingEmail} on
 *     the final bytes of every email given an `unsubscribe_url`, and every
 *     marketing email step of a flow is given one.
 *
 * Every marketing email, flow or newsletter, also carries the same header
 * pair, {@link oneClickUnsubscribeHeaders}.
 *
 * What it proves, and what it does not (review, 2026-10-23): the link is in
 * the markup a mail client reads, with words or a picture inside it. It does
 * not prove a reader SEES it: CSS (`display:none`), a `<template>` or
 * `<svg>`, Outlook's `[if !mso]` comments and invisible text can each hide
 * a link that is there. Reading CSS and each client's quirks is out of a
 * tokenizer's reach, and a correct bulletproof button wraps its link in
 * `[if !mso]` for every client but Outlook, so refusing that comment would
 * refuse good designs. The backstops are the plain text,
 * which every marketing flow email carries with the address clear of the
 * text after it, and the one-click header a mail client shows as its own
 * Unsubscribe button. The newsletter's check has the same edge, and the
 * same header.
 *
 * Pure, and it imports only `newsletter-html.ts`, so the dashboard can bundle
 * it.
 */

import { countLinks, scanHtml } from "./newsletter-html.js";

/** How a flow's marketing email writes its unsubscribe address. */
export const FLOW_OPT_OUT_URL_TOKEN = "{{ flow.opt_out_url }}";

/** The link every refusal tells staff to add. */
export const FLOW_OPT_OUT_LINK_EXAMPLE = `<a href="${FLOW_OPT_OUT_URL_TOKEN}">Unsubscribe</a>`;

/** RFC 8058's one value for `List-Unsubscribe-Post`. */
export const LIST_UNSUBSCRIBE_ONE_CLICK = "List-Unsubscribe=One-Click";

/**
 * The token as the flow interpolator reads it (spaces inside the braces are
 * optional; `marketing-flow-interpolation.ts`).
 */
const TOKEN_RE = /\{\{\s*flow\.opt_out_url\s*\}\}/u;
const TOKEN_ALL_RE = /\{\{\s*flow\.opt_out_url\s*\}\}/gu;
const WHOLE_TOKEN_RE = /^\{\{\s*flow\.opt_out_url\s*\}\}$/u;

/** Whether `text` carries the flow's unsubscribe token. */
export function hasFlowOptOutUrlToken(text: string): boolean {
  return TOKEN_RE.test(text);
}

/**
 * Punctuation that may close a sentence after the address. A mail client's
 * link finder leaves it off the link or keeps it, and the address works
 * either way: it ends in its base64url token, which has none of these
 * characters and verifies with one after it (`unsubscribe-tokens.ts`, pinned
 * in this module's tests). Anything else glued on is taken into the link:
 * an ASCII letter or digit, `-`, `_` or `/` stops the token verifying, and
 * the rest at best links text the writer meant to stand apart.
 */
const CLOSING_PUNCTUATION: ReadonlySet<string> = new Set([
  ".", ",", ";", ":", "!", "?", ")", "]", "}", ">", '"', "'", "`",
]);

/**
 * Whether an address that ends at `end` in `text` stands clear of the text
 * after it: closing punctuation at most, then a space, a tab, a line break
 * or the end of the text. Read by hand, so a long run of punctuation costs
 * one pass.
 */
function standsClear(text: string, end: number): boolean {
  let at = end;
  while (at < text.length && CLOSING_PUNCTUATION.has(text.charAt(at))) at += 1;
  const next = text.charAt(at);
  return next === "" || next === " " || next === "\t" || next === "\r" || next === "\n";
}

/**
 * Where a flow email's plain text stands on its unsubscribe token: `missing`
 * (none), `run_on` (every copy runs into the text after it, as in
 * `{{ flow.opt_out_url }}_x`, which a mail client would link as part of the
 * address) or `clear` (one copy stands clear, which is enough). The
 * template check and the flow editor's check both read it here.
 */
export function flowTextTokenState(text: string): "missing" | "run_on" | "clear" {
  let state: "missing" | "run_on" = "missing";
  for (const match of text.matchAll(TOKEN_ALL_RE)) {
    if (standsClear(text, match.index + match[0].length)) return "clear";
    state = "run_on";
  }
  return state;
}

/** {@link flowTextTokenState} for a text already filled in, against its address. */
function sentTextAddressState(text: string, url: string): "missing" | "run_on" | "clear" {
  let state: "missing" | "run_on" = "missing";
  for (let at = text.indexOf(url); at !== -1; at = text.indexOf(url, at + 1)) {
    if (standsClear(text, at + url.length)) return "clear";
    state = "run_on";
  }
  return state;
}

/**
 * What is wrong with `html`'s link to the unsubscribe address, in words.
 * `placeholderFills`, for a template, says which placeholders always fill in
 * as something: a link whose only words are other placeholders is refused,
 * since they can fill in as nothing and leave it with nothing to click
 * (review, 2026-10-23).
 */
function htmlLinkProblems(
  html: string,
  isTarget: (url: string, raw: string) => boolean,
  link: string,
  placeholderFills?: (placeholder: string) => boolean,
): string[] {
  const scan = scanHtml(html);
  const problems = scan.problems.map((p) => `the HTML ${p}`);
  const count = countLinks(scan.tags, isTarget);
  if (count.links === 0) {
    problems.push(`the HTML has no unsubscribe link: ${link} outside any comment`);
  } else if (count.clickable === 0) {
    problems.push(`the HTML's unsubscribe link has nothing to click: put its words inside ${link}`);
  } else if (
    placeholderFills !== undefined &&
    countLinks(scan.tags, isTarget, placeholderFills).clickable === 0
  ) {
    problems.push(
      `the HTML's unsubscribe link has only placeholders for words, and they can fill in as nothing: give it words of its own, as in ${link}`,
    );
  }
  return problems;
}

/**
 * What is wrong with a marketing email as it is WRITTEN — a saved edit, or a
 * flow step's own HTML and text — in plain words. Empty means it carries a
 * working unsubscribe link. It is refused when:
 *   - its HTML cannot be read (an unclosed tag, quote or comment), so the
 *     check never passes markup it did not see;
 *   - no `<a>` outside a comment has the token, quoted, as its WHOLE href.
 *     That refuses a pasted signed address (it unsubscribes whoever it was
 *     signed for), an href that adds to the token, a link inside a comment,
 *     and the token as bare text. Character references do not count either:
 *     the interpolator fills the token only as written;
 *   - the link has nothing to click, or only placeholders for words: a
 *     placeholder can fill in as nothing (a member with no phone), and the
 *     send check would then refuse the email, so its words must be its own.
 *     The token itself counts as words: it always fills, with the address;
 *   - a plain-text version is required (`textRequired`) and there is none,
 *     or there is one without the token, or one whose every token runs into
 *     the text after it ({@link flowTextTokenState}).
 */
export function checkMarketingEmailTemplate(args: {
  readonly html: string;
  readonly text: string | null;
  readonly textRequired: boolean;
}): ReadonlyArray<string> {
  const problems = htmlLinkProblems(
    args.html,
    (_url, raw) => WHOLE_TOKEN_RE.test(raw.trim()),
    FLOW_OPT_OUT_LINK_EXAMPLE,
    (placeholder) => WHOLE_TOKEN_RE.test(placeholder),
  );
  const text = args.text ?? "";
  if (text.trim().length === 0) {
    if (args.textRequired) {
      problems.push(
        `there is no plain-text version: it is sent with the HTML, so write one with ${FLOW_OPT_OUT_URL_TOKEN} in it`,
      );
    }
  } else {
    const state = flowTextTokenState(text);
    if (state === "missing") {
      problems.push(`the plain-text version has no ${FLOW_OPT_OUT_URL_TOKEN}`);
    } else if (state === "run_on") {
      problems.push(
        `the plain-text version runs ${FLOW_OPT_OUT_URL_TOKEN} into the text after it: follow it with a space or a line break, so a mail client does not take that text for part of the link`,
      );
    }
  }
  return problems;
}

/**
 * The same check on an email as it is SENT, against the address it must
 * offer: its HTML (when it has one) links to exactly `url`, read as a mail
 * client reads the href (character references decoded), with something to
 * click; its plain text (when it has one) carries `url` clear of the text
 * after it. Empty means it may go. The problems never quote the address: it
 * is signed for one member.
 */
export function checkSentMarketingEmail(args: {
  readonly html: string | undefined;
  readonly text: string | undefined;
  readonly url: string;
}): ReadonlyArray<string> {
  if (args.html === undefined && args.text === undefined) {
    return ["the email has no body to carry the unsubscribe link"];
  }
  const problems =
    args.html === undefined
      ? []
      : htmlLinkProblems(
          args.html,
          (url) => url === args.url,
          '<a href="(its unsubscribe address)">',
        );
  if (args.text !== undefined) {
    const state = sentTextAddressState(args.text, args.url);
    if (state === "missing") {
      problems.push("the plain-text version does not carry its unsubscribe address");
    } else if (state === "run_on") {
      problems.push(
        "the plain-text version runs its unsubscribe address into the text after it: a mail client would take that text for part of the link",
      );
    }
  }
  return problems;
}

/**
 * The header pair a marketing email carries (RFC 2369 and RFC 8058): a mail
 * client's own Unsubscribe button POSTs to `url`, which stops ALL marketing
 * (Mario, 2026-10-18), without opening a page. `url` must be https.
 */
export function oneClickUnsubscribeHeaders(url: string): {
  readonly "List-Unsubscribe": string;
  readonly "List-Unsubscribe-Post": string;
} {
  return {
    "List-Unsubscribe": `<${url}>`,
    "List-Unsubscribe-Post": LIST_UNSUBSCRIBE_ONE_CLICK,
  };
}
